import React from "react";
import { FaRupeeSign } from "react-icons/fa";
import StatusChip from "../../../components/StatusChip/StatusChip";
import Loader from "../../../components/Loader/Loader";
import { useStudentFees } from "./hooks/useStudentFees";
import "./StudentFees.css";

// StatusChip tones (see src/styles/tones.css) keyed by fee/bill status.
const STATUS_TONE = {
  Paid: "success",
  Due: "warning",
  Overdue: "danger",
  Pending: "warning",
};

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN")}`;

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Matches Figma nodes 1615:7539 / 1634:5451 ("Fees") in spirit — a fee-due
// summary plus a breakdown — rather than pixel-for-pixel, since the exact
// due-bills table fields in Figma aren't critical here. Renders only its own
// content — StudentPortalLayout already supplies the sidebar/header/shell.
const StudentFees = () => {
  const { fees, isLoading } = useStudentFees();

  if (isLoading) {
    return <Loader />;
  }

  if (!fees) {
    return (
      <div className="student-fees">
        <h2 className="sp-section-title">Fee Summary</h2>
        <p className="text-muted">No fee details are available right now.</p>
      </div>
    );
  }

  return (
    <div className="student-fees">
      <h2 className="sp-section-title">Fee Summary</h2>

      <div className="sp-card sp-fees-summary-card">
        <div className="sp-fees-summary-top">
          <span className="sp-icon-tile sp-fees-summary-icon" data-tone="primary">
            <FaRupeeSign />
          </span>
          <div className="sp-fees-summary-main">
            <span className="sp-fees-summary-label">Total Due</span>
            <span className="sp-fees-summary-amount">{formatCurrency(fees.totalDue)}</span>
          </div>
        </div>
        <div className="sp-fees-summary-meta">
          <span className="sp-fees-summary-due-date">Due by {formatDate(fees.dueDate)}</span>
          <StatusChip tone={STATUS_TONE[fees.status] || "secondary"}>{fees.status}</StatusChip>
        </div>
      </div>

      {fees.outstandingBills.length > 0 && (
        <>
          <h3 className="sp-section-title sp-fees-breakdown-title">Outstanding Bills</h3>
          <div className="sp-card sp-fees-breakdown-card">
            <BillList bills={fees.outstandingBills} dateField="dueDate" dateLabel="Due" />
          </div>
        </>
      )}

      <h3 className="sp-section-title sp-fees-breakdown-title">Payment History</h3>
      <div className="sp-card sp-fees-breakdown-card">
        {fees.previousBills.length > 0 ? (
          <BillList bills={fees.previousBills} dateField="paidAt" dateLabel="Paid" />
        ) : (
          <p className="text-muted">No past bills available.</p>
        )}
      </div>
    </div>
  );
};

const BillList = ({ bills, dateField, dateLabel }) => (
  <div className="sp-fees-breakdown-list">
    <div className="sp-fees-breakdown-row sp-fees-breakdown-row--header">
      <span>Bill</span>
      <span>Status</span>
      <span>Amount</span>
      <span>{dateLabel}</span>
    </div>
    {bills.map((bill) => (
      <div className="sp-fees-breakdown-row" key={bill.billNumber}>
        <span className="sp-fees-breakdown-label">
          {bill.programName} — {bill.term} ({bill.session})
          <span className="sp-fees-breakdown-bill-no">{bill.billNumber}</span>
        </span>
        <StatusChip tone={STATUS_TONE[bill.status] || "secondary"}>{bill.status}</StatusChip>
        <span className="sp-fees-breakdown-amount">{formatCurrency(bill.amount)}</span>
        <span className="sp-fees-breakdown-date">
          {formatDate(bill[dateField])}
          {bill.receiptLink && (
            <a href={bill.receiptLink} target="_blank" rel="noopener noreferrer" className="sp-fees-receipt-link">
              Receipt
            </a>
          )}
        </span>
      </div>
    ))}
  </div>
);

export default StudentFees;
